/**
 * `<WakingUpNotice>` — what a long wait for the server should look like.
 *
 * The api scales to zero, so the first request after a quiet period waits for a
 * container to boot: measured at ~29 seconds on prod against ~2 seconds warm.
 * Before this, that looked like a disabled button and nothing else — which is
 * how it got reported as "the quiz is stuck".
 *
 * Three deliberate choices:
 *
 *  - It BLOCKS at first. During a cold start nothing on the page can succeed,
 *    so letting someone keep clicking only produces more stuck requests and
 *    more doubt. But it can be put away (#598): "Keep working" collapses it to
 *    a corner pill that keeps counting, and clicking the pill brings it back.
 *    Someone reading or typing does not need the server, and a notice that
 *    owns the screen for a minute stops them doing either.
 *  - It counts UP, not down. We cannot honestly promise thirty seconds; a
 *    progress bar that fills and then keeps waiting is worse than no bar. An
 *    elapsed counter is always true.
 *  - It says why in the user's terms, and ONLY what was measured (#621). It
 *    used to say "waking the server up… about 30 seconds" for every long wait.
 *    On 20 Sep that was false for five minutes: the server was awake and a
 *    request was stuck, while a real start-up takes 3 to 4 seconds. Now the
 *    caller passes a `reason` from a probe that asks the server a question
 *    needing no database: `'busy'` (it answered, so it is up and the delay is
 *    ours), `'unreachable'` (it did not: starting up, or a slow connection,
 *    and the words do not pick one) or `'checking'`.
 *
 * Presentational only: it takes `open` and renders. The detection lives in
 * `@mangrove/trpc-client` (`useSlowRequest`), so this file has no data
 * dependency and can be told to open by anything.
 */
import * as React from 'react';
import { cn } from '../lib/utils.js';
import { WaitTips, type WaitTip } from './wait-tips.js';
import { useUiLabels, type UiLabels } from './ui-labels.js';

export interface WakingUpNoticeProps {
  /** True while a request has been outstanding past the slow threshold. */
  open: boolean;
  /** What the probe found. Without one, the notice claims no cause. */
  reason?: 'checking' | 'busy' | 'unreachable' | null;
  /**
   * After this long the copy stops sounding routine. Not a deadline — the
   * notice stays up until the request actually settles.
   */
  typicalSeconds?: number;
  /**
   * #814: tips to show while the wait lasts (HQ passes its own). Omitted, the
   * notice is as it was.
   */
  tips?: readonly WaitTip[];
  /** Which tip to start on (a list ordered for the person starts at 0). */
  tipsStartAt?: number;
  /** #828: "Take me there" on a tip that names a place. */
  onTipGo?: (tip: WaitTip) => void;
  /** #828: the place chosen, opened by the caller once the server answers. */
  chosenTip?: WaitTip | null;
  className?: string;
}

type Reason = 'checking' | 'busy' | 'unreachable';

/** #714: the notice's words, from the app's labels (English by default). */
function noticeWords(
  labels: UiLabels,
  why: Reason,
): { heading: string; body: string; overrun: string } {
  return {
    heading: why === 'busy' ? labels.serverSlowToAnswer : labels.waitingForServer,
    body:
      why === 'busy'
        ? labels.serverBusyBody
        : why === 'checking'
          ? labels.serverCheckingBody
          : labels.serverUnreachableBody,
    overrun: why === 'busy' ? labels.stillWaitingBusyBody : labels.stillWaitingBody,
  };
}

export function WakingUpNotice({
  open,
  reason,
  typicalSeconds = 30,
  tips,
  tipsStartAt,
  onTipGo,
  chosenTip,
  className,
}: WakingUpNoticeProps): React.JSX.Element | null {
  const labels = useUiLabels();
  const [elapsed, setElapsed] = React.useState(0);
  const [minimized, setMinimized] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setElapsed(0);
      // The next wake starts in full view: a new wait deserves its explanation.
      setMinimized(false);
      return;
    }
    const started = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  // Past the typical wait we stop implying it is nearly over and say so.
  const overrun = elapsed > typicalSeconds;
  const why = reason ?? 'unreachable';
  const words = noticeWords(labels, why);
  const heading = words.heading;

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className={cn(
          // #507: above the floating buttons, not on them.
          'float-toast flex items-center gap-2 rounded-full border bg-card px-3 py-2 text-xs shadow-lg',
          'text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
        aria-label={labels.waitPillLabel(heading, elapsed)}
        data-testid="waking-up-pill"
        data-reason={why}
      >
        <span
          aria-hidden="true"
          className="block size-3 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
        />
        <span>{heading}</span>
        <span className="tabular-nums">{labels.secondsShort(elapsed)}</span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] grid place-items-center bg-background/80 p-6 backdrop-blur-sm',
        className,
      )}
      role="status"
      aria-live="polite"
      data-testid="waking-up-notice"
      data-reason={why}
    >
      <div className="w-full max-w-sm rounded-xl border bg-card p-6 text-center shadow-lg">
        <span
          aria-hidden="true"
          className="mx-auto mb-4 block size-8 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
        />
        <h2 className="font-display text-base font-semibold tracking-tight">{heading}</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {overrun ? words.overrun : words.body}
        </p>
        <p
          className="mt-4 text-xs tabular-nums text-muted-foreground"
          data-testid="waking-up-elapsed"
        >
          {labels.secondsShort(elapsed)}
        </p>
        {tips && tips.length > 0 ? (
          <WaitTips
            tips={tips}
            startAt={tipsStartAt}
            onGo={onTipGo}
            chosen={chosenTip}
            className="mt-4"
          />
        ) : null}
        <button
          type="button"
          onClick={() => setMinimized(true)}
          className="mt-4 rounded-md px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="waking-up-minimize"
        >
          {labels.keepWorking}
        </button>
      </div>
    </div>
  );
}
